import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, X, Archive, Sparkles, Check, Wand2 } from "lucide-react";

const TARGET_OPTIONS = [];
for (let v = 2.5; v <= 5.5; v += 0.25) TARGET_OPTIONS.push(Math.round(v * 4) / 4);

/**
 * Coach-facing milestones card (iter64). Renders inline on PlayerDetail so the
 * coach can create/activate/archive milestones + toggle Next-Focus behaviour
 * without leaving the player profile.
 */
export default function MilestonesCard({ studentId, sport, studentName }) {
  const [data, setData] = useState({ milestones: [], current_axes: {}, axes_order: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // milestone being edited or null
  // Iter65: AI draft state. When populated, MilestoneForm renders in
  // "review draft" mode with the suggested name + targets + reasoning banner.
  const [suggesting, setSuggesting] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/coach/players/${studentId}/milestones`, { params: { sport } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load milestones");
    } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, [studentId, sport]);

  const setActive = async (id, next) => {
    try {
      await api.patch(`/milestones/${id}`, { is_active: next });
      await load();
      toast.success(next ? "Milestone activated — will drive Next Focus" : "Deactivated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const setMode = async (id, mode) => {
    try {
      await api.patch(`/milestones/${id}`, { next_focus_mode: mode });
      await load();
      toast.success(mode === "override" ? "Next Focus: milestone only" : "Next Focus: milestone + your note");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this milestone? Progress data stays in the audit log; the student will no longer see it.")) return;
    try {
      await api.delete(`/milestones/${id}`);
      await load();
      toast.success("Milestone archived");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const suggestWithAI = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/coach/players/${studentId}/milestones/suggest`, null, { params: { sport } });
      setAiDraft(r.data);
      setShowForm(true);
      setEditing(null);
      toast.success("Claude drafted a milestone — review and save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI suggestion failed");
    } finally { setSuggesting(false); }
  };

  return (
    <div className="card-surface p-6 fade-up" data-testid="milestones-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono">Custom milestones · {sport?.toUpperCase()}</div>
        </div>
        {!showForm && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={suggestWithAI}
              disabled={suggesting}
              data-testid="milestone-suggest-ai"
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 inline-flex items-center gap-1 border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Draft a milestone using Claude based on this student's radar weaknesses"
            >
              <Wand2 className="w-3 h-3" /> {suggesting ? "Drafting…" : "Suggest with AI"}
            </button>
            <button
              onClick={() => { setAiDraft(null); setShowForm(true); }}
              data-testid="milestone-new"
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 inline-flex items-center gap-1"
              style={{ background: "#CCFF00", color: "#0A0A0A" }}
            >
              <Plus className="w-3 h-3" /> New milestone
            </button>
          </div>
        )}
      </div>

      {loading && <div className="text-xs text-[#A3A3A3]">Loading…</div>}

      {(showForm || editing) && (
        <MilestoneForm
          studentId={studentId}
          sport={sport}
          axesOrder={data.axes_order}
          initial={editing || (aiDraft ? {
            name: aiDraft.name,
            targets: aiDraft.targets,
            is_active: aiDraft.is_active,
          } : null)}
          aiReasoning={editing ? null : aiDraft?.reasoning}
          saving={saving}
          setSaving={setSaving}
          onCancel={() => { setShowForm(false); setEditing(null); setAiDraft(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); setAiDraft(null); await load(); }}
        />
      )}

      {!loading && data.milestones.length === 0 && !showForm && (
        <div className="text-xs text-[#A3A3A3]">
          No milestones yet for {studentName || "this player"}. Create one like &ldquo;Kitchen Play Master&rdquo; or &ldquo;Solid Return Game&rdquo; — the student sees progress bars on their roadmap as their DUPR levels climb toward the targets you set.
        </div>
      )}

      {!loading && data.milestones.length > 0 && (
        <div className="space-y-3 mt-3">
          {data.milestones.map((m) => (
            <MilestoneRow
              key={m.id}
              milestone={m}
              onEdit={() => setEditing(m)}
              onActivate={(next) => setActive(m.id, next)}
              onMode={(mode) => setMode(m.id, mode)}
              onArchive={() => archive(m.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MilestoneRow({ milestone, onEdit, onActivate, onMode, onArchive }) {
  const prog = milestone.progress || { per_axis: [], aggregate: 0, biggest_gap: null };
  const active = milestone.is_active;
  return (
    <div
      className="border p-3"
      style={{ borderColor: active ? "#CCFF00" : "#262626", background: active ? "rgba(204,255,0,0.03)" : "transparent" }}
      data-testid={`milestone-row-${milestone.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {active && <Sparkles className="w-3.5 h-3.5 text-[#CCFF00]" />}
          <div className="text-sm font-semibold">{milestone.name}</div>
          {milestone.focus_area && (
            <span
              data-testid={`milestone-focus-chip-${milestone.id}`}
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3]"
            >
              {milestone.focus_area}{milestone.sub_focus ? ` · ${milestone.sub_focus}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onActivate(!active)}
            data-testid={`milestone-toggle-active-${milestone.id}`}
            className="text-[10px] uppercase tracking-wider px-2 py-1 font-bold"
            style={{
              background: active ? "#CCFF00" : "transparent",
              color: active ? "#0A0A0A" : "#A3A3A3",
              border: active ? "1px solid #CCFF00" : "1px solid #262626",
            }}
          >
            {active ? "Active" : "Activate"}
          </button>
          <button
            onClick={onEdit}
            data-testid={`milestone-edit-${milestone.id}`}
            className="text-[10px] uppercase tracking-wider px-2 py-1 font-bold border border-[#262626] hover:border-[#CCFF00] text-[#A3A3A3]"
          >
            Edit
          </button>
          <button
            onClick={onArchive}
            data-testid={`milestone-archive-${milestone.id}`}
            className="text-[10px] uppercase tracking-wider px-2 py-1 font-bold border border-[#262626] hover:border-[#FF4500] text-[#A3A3A3] inline-flex items-center gap-1"
            title="Archive"
          >
            <Archive className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Aggregate progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] text-[#A3A3A3]">Overall</div>
          <div className="text-[10px] font-bold" style={{ color: "#CCFF00" }}>{prog.aggregate}%</div>
        </div>
        <div className="h-1.5 bg-[#141414] overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${prog.aggregate}%`, background: "#CCFF00" }} />
        </div>
      </div>

      {/* Iter74 Phase B — coach's target-note is prominently displayed above
          the per-axis bars so students know what "done" looks like. */}
      {milestone.target_note && (
        <div
          data-testid={`milestone-target-note-${milestone.id}`}
          className="mb-3 text-xs italic text-[#E5E5E5] border-l-2 border-[#CCFF00] pl-2 py-1 bg-[#0A0A0A]"
        >
          &ldquo;{milestone.target_note}&rdquo;
        </div>
      )}

      {/* Per-axis mini bars */}
      <div className="space-y-1.5">
        {prog.per_axis.map((a) => (
          <div key={a.axis}>
            <div className="flex items-center justify-between text-[10px] text-[#A3A3A3]">
              <div>{a.axis}</div>
              <div>{a.current == null ? "—" : a.current} / {a.target}</div>
            </div>
            <div className="h-1 bg-[#141414] overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${a.progress}%`, background: a.progress >= 100 ? "#CCFF00" : "#66C2FF" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Next-focus mode toggle — only relevant when active */}
      {active && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#262626] gap-2 flex-wrap">
          <div className="text-[10px] text-[#A3A3A3]">
            Next Focus behavior:
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onMode("auto_prepend")}
              data-testid={`milestone-mode-prepend-${milestone.id}`}
              className="text-[10px] uppercase tracking-wider px-2 py-1 font-bold border"
              style={{
                borderColor: (milestone.next_focus_mode !== "override") ? "#CCFF00" : "#262626",
                color: (milestone.next_focus_mode !== "override") ? "#CCFF00" : "#A3A3A3",
              }}
            >
              {(milestone.next_focus_mode !== "override") && <Check className="w-3 h-3 inline mr-1" />}
              Prepend to note
            </button>
            <button
              onClick={() => onMode("override")}
              data-testid={`milestone-mode-override-${milestone.id}`}
              className="text-[10px] uppercase tracking-wider px-2 py-1 font-bold border"
              style={{
                borderColor: milestone.next_focus_mode === "override" ? "#CCFF00" : "#262626",
                color: milestone.next_focus_mode === "override" ? "#CCFF00" : "#A3A3A3",
              }}
            >
              {milestone.next_focus_mode === "override" && <Check className="w-3 h-3 inline mr-1" />}
              Override note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MilestoneForm({ studentId, sport, axesOrder, initial, aiReasoning, saving, setSaving, onCancel, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [targets, setTargets] = useState(
    initial?.targets?.length
      ? initial.targets.map((t) => ({ axis: t.axis, target: t.target }))
      : [{ axis: (axesOrder[0] || ""), target: 3.5 }],
  );
  const [isActive, setIsActive] = useState(initial ? Boolean(initial.is_active) : true);
  // Iter74 Phase B — canonical Focus Area / Sub-focus / Target note
  const [focusArea, setFocusArea] = useState(initial?.focus_area || "");
  const [subFocus, setSubFocus] = useState(initial?.sub_focus || "");
  const [targetNote, setTargetNote] = useState(initial?.target_note || "");
  const [focusTree, setFocusTree] = useState({ zones: [] });

  // Load the two-level dropdown data once. Cached in the tree state and only
  // fetched again on remount so switching students doesn't spam the endpoint.
  useEffect(() => {
    let cancelled = false;
    api.get("/pathway/focus-areas")
      .then((r) => { if (!cancelled) setFocusTree(r.data || { zones: [] }); })
      .catch(() => { /* ignore — form still works without the dropdown */ });
    return () => { cancelled = true; };
  }, []);

  const currentZone = focusTree.zones.find((z) => z.key === focusArea);
  const subOptions = currentZone?.sub_focus || [];

  // When coach picks a Focus Area, auto-seed the target axis + a friendly
  // default name if the coach hasn't typed one yet. Small UX win — cuts a
  // click off the common case.
  const applyFocusArea = (val) => {
    setFocusArea(val);
    setSubFocus("");
    if (val && !name.trim()) setName(`${val} focus`);
    if (val && targets.length === 1 && !targets[0].axis) {
      setTargets([{ axis: val, target: 3.5 }]);
    } else if (val && !targets.some((t) => t.axis === val)) {
      // Add the picked zone as a target axis if not already present.
      setTargets((prev) => [{ axis: val, target: 3.5 }, ...prev.filter((t) => t.axis !== val)]);
    }
  };

  // Only PATCH when the initial actually came from a saved milestone (has id).
  // AI drafts are pre-fills without id — they still hit POST /create.
  const isEditing = Boolean(initial && initial.id);

  const addTarget = () => {
    const used = new Set(targets.map((t) => t.axis));
    const next = axesOrder.find((a) => !used.has(a));
    if (!next) { toast.error("All axes already added"); return; }
    setTargets([...targets, { axis: next, target: 3.5 }]);
  };
  const updateTarget = (i, patch) => setTargets(targets.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const removeTarget = (i) => setTargets(targets.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!name.trim()) { toast.error("Give the milestone a name"); return; }
    if (targets.length === 0) { toast.error("Add at least one target axis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        sport,
        targets: targets.map((t) => ({ axis: t.axis, target: Number(t.target) })),
        is_active: isActive,
        focus_area: focusArea || null,
        sub_focus: subFocus || null,
        target_note: targetNote.trim() || null,
      };
      if (isEditing) {
        await api.patch(`/milestones/${initial.id}`, payload);
        toast.success("Milestone updated");
      } else {
        await api.post(`/coach/players/${studentId}/milestones`, payload);
        toast.success("Milestone created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-[#CCFF00] p-4 mb-3" data-testid="milestone-form">
      {aiReasoning && (
        <div className="mb-3 border border-[#66C2FF] bg-[#0A0A0A] p-3" data-testid="ai-reasoning">
          <div className="label-mono text-[10px] mb-1" style={{ color: "#66C2FF" }}>
            AI DRAFT · CLAUDE&apos;S RATIONALE
          </div>
          <div className="text-xs text-[#A3A3A3] italic">&ldquo;{aiReasoning}&rdquo;</div>
          <div className="text-[10px] text-[#525252] mt-1">Edit anything below before saving — Claude&apos;s a suggestion, not a decree.</div>
        </div>
      )}

      {/* Iter74 Phase B — canonical Focus Area picker (zone → sub-focus). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="label-mono text-[10px] mb-1">Focus area</div>
          <select
            value={focusArea}
            onChange={(e) => applyFocusArea(e.target.value)}
            data-testid="milestone-focus-area-select"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
          >
            <option value="">Freeform (no zone)</option>
            {focusTree.zones.map((z) => (
              <option key={z.key} value={z.key}>{z.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-mono text-[10px] mb-1">Sub-focus (specific skill)</div>
          <select
            value={subFocus}
            onChange={(e) => setSubFocus(e.target.value)}
            data-testid="milestone-sub-focus-select"
            disabled={!subOptions.length}
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] disabled:opacity-40"
          >
            <option value="">
              {subOptions.length ? "Pick a specific skill…" : "Pick a focus area first"}
            </option>
            {subOptions.map((s) => (
              <option key={s.skill_id} value={s.value}>{s.label} · L{s.level}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <div className="label-mono text-[10px] mb-1">Name</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Kitchen Play Master"
          data-testid="milestone-name-input"
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
        />
      </div>

      <div className="mb-3">
        <div className="label-mono text-[10px] mb-1">Target note (what &ldquo;done&rdquo; looks like)</div>
        <input
          value={targetNote}
          onChange={(e) => setTargetNote(e.target.value.slice(0, 280))}
          placeholder="e.g. 75% success rate on cross-court dinks in a 10-ball rally"
          data-testid="milestone-target-note-input"
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
        />
        {targetNote && (
          <div className="text-[10px] text-[#525252] mt-1">{targetNote.length}/280 characters</div>
        )}
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <div className="label-mono text-[10px]">Target axes ({targets.length})</div>
          <button
            onClick={addTarget}
            data-testid="milestone-add-axis"
            className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add axis
          </button>
        </div>
        <div className="space-y-2">
          {targets.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`milestone-target-${i}`}>
              <select
                value={t.axis}
                onChange={(e) => updateTarget(i, { axis: e.target.value })}
                className="flex-1 bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs focus:outline-none focus:border-[#CCFF00]"
              >
                {axesOrder.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <select
                value={t.target}
                onChange={(e) => updateTarget(i, { target: Number(e.target.value) })}
                className="w-24 bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs focus:outline-none focus:border-[#CCFF00]"
              >
                {TARGET_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o.toFixed(2)}</option>
                ))}
              </select>
              <button
                onClick={() => removeTarget(i)}
                className="text-[#A3A3A3] hover:text-[#FF4500]"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="mb-3">
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="milestone-active-checkbox"
          />
          <span>Set as the ACTIVE milestone (drives Next Focus)</span>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-[10px] uppercase tracking-wider px-3 py-1.5 font-bold border border-[#262626] hover:border-[#FF4500] text-[#A3A3A3]"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="milestone-save"
          className="text-[10px] uppercase tracking-wider px-3 py-1.5 font-bold disabled:opacity-50"
          style={{ background: "#CCFF00", color: "#0A0A0A" }}
        >
          {saving ? "Saving…" : isEditing ? "Save changes" : "Create milestone"}
        </button>
      </div>
    </div>
  );
}
